import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { convertUtcToIstWithTime } from "../../utilities";

const STATUS_COLORS = {
  SUCCESS:   { bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  INITIATED: { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  FAILED:    { bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
};

const PACK_ICONS = {
  single_unlock: "🔓",
  spark_bundle:  "✨",
  premium:       "💎",
  elite:         "👑",
  annual:        "🏆",
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] ?? { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status}
    </span>
  );
};

const PackPurchasesList = () => {
  const navigate = useNavigate();
  const [orders, setOrders]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch]       = useState("");
  const [inputVal, setInputVal]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading]     = useState(false);
  const limit = 20;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit });
      if (search)       params.set("q", search);
      if (statusFilter) params.set("status", statusFilter);

      const res = await func.get(`${urls.SEARCH_PACK_PURCHASES_URL}?${params.toString()}`);
      if (res.status === 200) {
        setOrders(res.data.data ?? []);
        setTotal(res.data.total ?? 0);
        setTotalPages(res.data.totalPages ?? 1);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputVal);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pack Purchases</h1>
        <p className="text-sm text-gray-500 mt-1">
          Search by transaction ID, phone number, or user name
        </p>
      </div>

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder="Transaction ID (MM2605130001), phone, or name…"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setInputVal(""); setSearch(""); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </form>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="INITIATED">INITIATED</option>
          <option value="FAILED">FAILED</option>
        </select>
      </div>

      {/* Stats strip */}
      <div className="mb-4 text-sm text-gray-500">
        {loading ? "Loading…" : `${total} transaction${total !== 1 ? "s" : ""} found`}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Transaction ID", "User", "Phone", "Pack", "Amount", "Status", "Date", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    No transactions found
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr
                    key={o._id}
                    className="hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => navigate(`/payments/pack-purchases/detail/${o._id}`)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono font-semibold text-blue-700">
                        {o.readableTransactionId ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-medium">
                      {o.seekerName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {o.userPhone ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5">
                        <span>{PACK_ICONS[o.packType] ?? "📦"}</span>
                        <span className="font-medium text-gray-700">{o.packLabel ?? o.packType}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      ₹{(o.amount ?? 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={o.payment_status} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {o.createdAt ? convertUtcToIstWithTime(o.createdAt) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        className="text-blue-600 text-xs font-semibold hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/payments/pack-purchases/detail/${o._id}`);
                        }}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                ← Prev
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackPurchasesList;
